// src/components/messaging/ConversationList.tsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { List, Avatar, Badge, Typography, Input, Space, Button, Empty, Tag, Tooltip } from "antd";
import { SearchOutlined, UserOutlined, TeamOutlined, ClockCircleOutlined, CheckCircleOutlined, MessageOutlined } from "@ant-design/icons";
import { Conversation } from "../../types/messaging";
import { getConversationTitle, getConversationParticipantNames, getTimeAgo } from "../../api/messaging";
import { useMessagingPanel } from "../../context/MessagingContext";
import "./MessagingPanel.css";
import type { UserUuid } from "../../types/identity";

const { Text } = Typography;

interface ConversationListProps {
	conversations: Conversation[];
	queueConversations: Conversation[];
	activeConversationId: string | null;
	onSelectConversation: (conversationId: string) => void;
	currentUserId: UserUuid;
	isPatient: boolean;
	onNewConversation: () => void;
	notice?: React.ReactNode;
}

const ConversationList: React.FC<ConversationListProps> = ({ conversations, queueConversations, activeConversationId, onSelectConversation, currentUserId, isPatient, onNewConversation, notice }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const { panelTab, setPanelTab: setActiveTab, highlightConversationId, clearHighlight } = useMessagingPanel();
	// Patients have no Queue tab, whatever tab a caller asked for.
	const activeTab = isPatient ? "my" : panelTab;
	const contentRef = useRef<HTMLDivElement>(null);

	const displayConversations = activeTab === "queue" ? queueConversations : conversations;

	const filteredConversations = useMemo(() => {
		if (!searchQuery.trim()) return displayConversations;

		const query = searchQuery.toLowerCase();
		return displayConversations.filter((conv) => {
			const title = getConversationTitle(conv, currentUserId).toLowerCase();
			const participants = getConversationParticipantNames(conv, currentUserId).toLowerCase();
			return title.includes(query) || participants.includes(query);
		});
	}, [displayConversations, searchQuery, currentUserId]);

	const highlightedId =
		activeTab === "queue" && highlightConversationId && filteredConversations.some((c) => c.id === highlightConversationId)
			? highlightConversationId
			: null;

	useEffect(() => {
		if (!highlightedId) return;
		const rows = contentRef.current?.querySelectorAll<HTMLElement>("[data-conversation-id]") ?? [];
		const row = Array.from(rows).find((el) => el.dataset.conversationId === highlightedId);
		row?.scrollIntoView?.({ block: "nearest" });
	}, [highlightedId]);

	const getConversationStatus = (conv: Conversation) => {
		if (conv.assignment) {
			if (conv.assignment.status === "unassigned") {
				return (
					<Tag color="orange" icon={<ClockCircleOutlined />}>
						Unclaimed
					</Tag>
				);
			}
			if (conv.assignment.assigned_to_user_id === currentUserId) {
				return (
					<Tag color="green" icon={<CheckCircleOutlined />}>
						Assigned to you
					</Tag>
				);
			}
		}
		return null;
	};

	const getLastMessagePreview = (conv: Conversation) => {
		if (!conv.last_message) return "No messages yet";

		const content = conv.last_message.content || "";
		const stripped = content.replace(/<[^>]*>/g, "");
		return stripped.length > 50 ? stripped.substring(0, 50) + "..." : stripped;
	};

	return (
		<div className="conversation-list">
			<div className="conversation-list-header">
				{notice}
				<Input placeholder="Search conversations..." prefix={<SearchOutlined />} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear className="conversation-search" />

				{!isPatient && (
					<Space className="conversation-tabs">
						<Button data-testid="messaging-tab-my" type={activeTab === "my" ? "primary" : "default"} size="small" onClick={() => setActiveTab("my")}>
							My Conversations ({conversations.length})
						</Button>
						<Button data-testid="messaging-tab-queue" type={activeTab === "queue" ? "primary" : "default"} size="small" onClick={() => setActiveTab("queue")}>
							Queue ({queueConversations.length})
						</Button>
					</Space>
				)}

				{!isPatient && activeTab === "my" && (
					<Button type="primary" size="small" onClick={onNewConversation} block>
						New Conversation
					</Button>
				)}

				{isPatient && (
					<Button type="primary" size="small" onClick={onNewConversation} block>
						New Message Request
					</Button>
				)}
			</div>

			<div className="conversation-list-content" ref={contentRef}>
				{filteredConversations.length === 0 ? (
					<Empty
						image={<MessageOutlined style={{ fontSize: 36, color: "var(--mg-text-disabled)" }} />}
						description={<Text type="secondary">{searchQuery ? "No conversations match your search" : activeTab === "queue" ? "No conversations in queue" : "No conversations yet"}</Text>}
					/>
				) : (
					<List
						dataSource={filteredConversations}
						renderItem={(conv) => {
							const isActive = conv.id === activeConversationId;
							const isHighlighted = conv.id === highlightedId;
							const unreadCount = conv.unread_count || 0;
							const title = getConversationTitle(conv, currentUserId);
							const participants = getConversationParticipantNames(conv, currentUserId);

							// For direct conversations show the other person's avatar
							const otherParticipant = conv.type !== 'group'
								? (conv.participants || []).find((p) => p.user_id !== currentUserId)
								: null;
							const avatarSrc = otherParticipant?.user?.profile_picture || undefined;

							return (
								<List.Item
									data-conversation-id={conv.id}
									data-highlighted={isHighlighted || undefined}
									className={`conversation-list-item ${isActive ? "active" : ""}`}
									style={isHighlighted ? { background: "var(--mg-warning-bg)", borderLeftColor: "var(--mg-warning)" } : undefined}
									onClick={() => {
										clearHighlight();
										onSelectConversation(conv.id);
									}}
								>
									<div className="conversation-item-content">
										<div className="conversation-item-avatar">
											<Badge count={unreadCount} size="small" offset={[-5, 5]}>
												<Avatar
													size={40}
													icon={!avatarSrc ? (conv.type === "group" ? <TeamOutlined /> : <UserOutlined />) : undefined}
													src={avatarSrc}
													className={unreadCount > 0 ? "has-unread" : ""}
												/>
											</Badge>
										</div>

										<div className="conversation-item-details">
											<div className="conversation-item-header">
												<Text strong className="conversation-item-title">
													{title}
												</Text>
												{conv.last_message_at && (
													<Text type="secondary" className="conversation-item-time">
														{getTimeAgo(conv.last_message_at)}
													</Text>
												)}
											</div>

											<div className="conversation-item-subtitle">
												<Text type="secondary" className="conversation-item-preview" ellipsis>
													{getLastMessagePreview(conv)}
												</Text>
											</div>

											{activeTab === "queue" && <div className="conversation-item-status">{getConversationStatus(conv)}</div>}
										</div>
									</div>
								</List.Item>
							);
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default ConversationList;
